import { UniversalRoll } from "@/pages/catalogs/models/UniversalRoll";
import { RollStatusTag } from "@/pages/catalogs/shared/components/RollStatusTag";
import { rollStatusCodes } from "@/shared/selectors/RollStatusSelector";
import { ErrorModal } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Part } from "../../models/Part";
import { getUniversalRolls } from "../../repositories/parts/UniversalRollRepository";
import { rollPositionCodes } from "@/shared/selectors/RollPositionSelector";
import { RollPositionTag } from "@/pages/catalogs/shared/components/RollPositionTag";

type useRollsProps = {
	partId: number;
	onError: (error: Error) => void;
	onSuccess?: (data: UniversalRoll[]) => void;
};

export const useRolls = ({ partId, onError, onSuccess }: useRollsProps) => {
	return useQuery<UniversalRoll[], Error>({
		queryKey: ["universal-rolls", partId],
		queryFn: ({ signal }) => getUniversalRolls({ partId }, signal),
		onError: onError,
		onSuccess: onSuccess,
	});
};

type Props = {
	part: Part | null;
};

export const SpecificUniversalRollData = ({ part }: Props) => {
	const [error, setError] = useState("");
	const [roll, setRoll] = useState<UniversalRoll>();

	const { data: rolls } = useRolls({
		partId: part?.PartId!,
		onError: (error) => setError(error.message),
		onSuccess: (data: UniversalRoll[]) => {
			setRoll(data[0]);
		},
	});

	return (
		<>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
					<TextField
						value={roll?.Stands || ""}
						label="Stands"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
					<TextField
						value={roll?.Sections || ""}
						label="Sections"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 3 }}>
					<TextField
						value={
							rollPositionCodes.find(
								(status) => status.Code === roll?.RollPosition,
							)?.Name || ""
						}
						label="Position"
						variant="outlined"
						size="small"
						fullWidth
						disabled
						InputProps={{
							startAdornment: (
								<span
									style={{
										width: "25px",
										height: "25px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginRight: "8px",
										marginLeft: "8px",
									}}
								>
									<RollPositionTag
										position={{
											Code:
												rollPositionCodes.find(
													(status) => status.Code === roll?.RollPosition,
												)?.Code || "",
											Name:
												rollPositionCodes.find(
													(status) => status.Code === roll?.RollPosition,
												)?.Name || "",
										}}
									/>
								</span>
							),
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 3 }}>
					<TextField
						value={
							rollStatusCodes.find(
								(status) => status.Code === roll?.RollStatusCode,
							)?.Name || ""
						}
						label="Status"
						variant="outlined"
						size="small"
						fullWidth
						disabled
						InputProps={{
							startAdornment: (
								<span
									style={{
										width: "25px",
										height: "25px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginRight: "8px",
										marginLeft: "8px",
									}}
								>
									<RollStatusTag
										status={{
											Code:
												rollStatusCodes.find(
													(status) => status.Code === roll?.RollStatusCode,
												)?.Code || "",
											Name:
												rollStatusCodes.find(
													(status) => status.Code === roll?.RollStatusCode,
												)?.Name || "",
										}}
									/>
								</span>
							),
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 3 }}>
					<TextField
						value={roll?.DonutPartName || ""}
						label="Donut"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 3 }}>
					<TextField
						value={roll?.ArborPartName || ""}
						label="Arbor"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
